import { LIST_AVAILABLE_PRODUCTS_QUERY_ERROR } from '@/hooks/products/constants';
import { useResetProductQuantitiesOnMount, useSelectedProductQuantities } from '@/hooks/products/useProductQuantity';
import { UNKNOWN_SUPPLIER_NAME } from '@/pages/umeds/order-medication/util';
import { ESCRIPT_REFILL_PHARMACY_ROUTE } from '@/routes/route-paths';
import type { MedicationDto } from '@montugroup/circuit-api-contracts';
import type { ErrorDisplay } from '@montugroup/circuit-shared/context/ErrorManagement';
import { useErrorManagement } from '@montugroup/circuit-shared/context/ErrorManagement';
import { GoogleAnalyticsEventName } from '@montugroup/circuit-shared/types/index';
import type { AsyncContent } from '@montugroup/circuit-shared/types/refill';
import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleAnalyticsEscriptEventName } from '../analytics/types';
import useGoogleAnalytics from '../analytics/useGoogleAnalytics';
import { hashedEmail, hashedGooglePhone, hashedItem, hashedMetaPhone } from '../analytics/utils';
import type { PersistedProductDetails } from '../patient/refill/persistedOrderDetails';
import { managePersistedOrderDetails } from '../patient/refill/persistedOrderDetails';
import type { Customer } from '../user/useCustomer';
import type { ListProductsResponse, PlaceOrderFn, Product, SelectedProduct, ValidateOrderFn } from './types';
import { IssueStatuses } from './types';
import { useListAvailableProducts } from './useListAvailableProducts';

export const generateSelectedProducts = (
  productSelection: Map<number, number>,
  products: Product[],
): SelectedProduct[] => {
  const selectedProducts: SelectedProduct[] = [];
  productSelection.forEach((quantity, productId) => {
    const product = products.find((prd) => prd.id === productId);
    if (!product || quantity === 0 || product.productIssueStatus === IssueStatuses.CONSUMED) {
      return;
    }

    selectedProducts.push({
      id: productId,
      data: {
        ...product,
        product_name: product.name,
        quantity,
        product_id: product.id,
        brand: product.brand || UNKNOWN_SUPPLIER_NAME,
        is_concession: product.isConcession,
        prescriptionId: 'prescriptionId' in product ? (product as MedicationDto).prescriptionId : undefined,
      },
    });
  });
  return selectedProducts;
};

export const generatePersistedProductDetails = (
  productSelection: Map<number, number>,
  products: Product[],
): PersistedProductDetails[] => {
  const selectedProducts: PersistedProductDetails[] = [];
  productSelection.forEach((quantity, productId) => {
    const product = products.find((prd) => prd.id === productId);
    if (!product || quantity === 0 || product.productIssueStatus === IssueStatuses.CONSUMED) {
      return;
    }

    selectedProducts.push({
      id: product.id,
      name: product.name,
      quantity,
      price: product.price,
      brand: product.brand || UNKNOWN_SUPPLIER_NAME,
      is_concession: product.isConcession,
      prescriptionId: 'prescriptionId' in product ? (product as MedicationDto).prescriptionId : undefined,
    });
  });
  return selectedProducts;
};

export interface EScriptOrderMedicationController {
  state: {
    refillResponse: AsyncContent<ListProductsResponse>;
    isSubmitting: boolean;
    selectedProducts: SelectedProduct[];
    hasSelectedAnyProducts: boolean;
    // These are exposed for unit testing, but shouldn't be accessed in production code
    __testing: {
      productSelection: Map<number, number>;
    };
  };

  actions: {
    submitOrder: (params: { validateOrderCallback: ValidateOrderFn; customer?: Customer }) => Promise<void>;
    validateOrder: ValidateOrderFn;
    enqueueError: (error: ErrorDisplay) => void;
    refetchRefillResponse: () => void;

    // These are exposed for unit testing, but should never be called
    __testing: {
      setIsSubmitting: Dispatch<SetStateAction<boolean>>;
    };
  };
}

export const useEScriptOrderMedicationController = (): EScriptOrderMedicationController => {
  const navigate = useNavigate();
  const { enqueueError } = useErrorManagement();
  const { sendGoogleAnalyticsGTagEvent, sendGoogleAnalyticsEvent } = useGoogleAnalytics();
  const persistedOrderDetails = managePersistedOrderDetails();

  const productSelection = useSelectedProductQuantities();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const refillResponseFromHook = useListAvailableProducts({
    meta: {
      error: LIST_AVAILABLE_PRODUCTS_QUERY_ERROR,
    },
  });

  const refillResponse = refillResponseFromHook.response;

  const refillAPIData = refillResponse.data;
  const refetchRefillResponse = refillResponseFromHook.refetch;

  const selectedProducts = generateSelectedProducts(productSelection, [
    ...(refillAPIData?.medications ?? []),
    ...(refillAPIData?.devices ?? []),
  ]);
  const hasSelectedAnyProducts = selectedProducts.filter((x) => x.data.product_id !== null).length > 0;

  useResetProductQuantitiesOnMount();

  // Persist the current product selection for the downstream shipping/delivery pages.
  // Nothing reads it back into the quantity store on load: order pre-filling was
  // removed on both customer types (#7647), so the page always starts empty.
  useEffect(() => {
    const persistedProductDetails = [
      ...generatePersistedProductDetails(productSelection, [
        ...(refillAPIData?.medications ?? []),
        ...(refillAPIData?.devices ?? []),
      ]),
    ];

    // TODO: Properly set CreditDiscounts and SASApproval boolean
    persistedOrderDetails.setOrderDetails(persistedProductDetails, [], false);
  }, [selectedProducts]);

  // Last chance to stop order submission, perform validation logic here.
  const validateOrder = useCallback(
    async (placeOrderCallback: PlaceOrderFn): Promise<void> => {
      try {
        setIsSubmitting(true);

        await placeOrderCallback();
      } catch (error) {
        //logger.error(error);
        setIsSubmitting(false);
        throw error;
      }
    },
    [setIsSubmitting],
  );

  const submitOrder = async (params: { validateOrderCallback: ValidateOrderFn; customer?: Customer }) => {
    const { validateOrderCallback, customer } = params;

    if (isSubmitting || !refillAPIData) {
      return;
    }

    // Send event via Google Analytics
    // TODO: Confirm that this link ID can be reused from mScript Order Med page
    sendGoogleAnalyticsGTagEvent(GoogleAnalyticsEventName.CLICK_CTA, {
      link_id: 'cart_checkout_button',
    });

    const selectedData = persistedOrderDetails.getOrderDetails();
    const totalPrice = selectedData?.selectedProducts.reduce((sum, product) => sum + Number(product.price), 0) ?? 0;

    // start order GA event
    sendGoogleAnalyticsEvent(GoogleAnalyticsEscriptEventName.ORDER_INITIATED, {
      sha_email: customer?.email ? hashedEmail(customer.email) : '',
      sha_fb_phone_number: customer?.phone ? hashedMetaPhone(customer.phone) : '',
      sha_gads_phone_number: customer?.phone ? hashedGooglePhone(customer.phone) : '',
      circuit_identifier: customer?.code ? hashedItem(customer.code) : '',
      order_amnt: totalPrice,
    });

    await validateOrderCallback(async () => {
      navigate({
        pathname: ESCRIPT_REFILL_PHARMACY_ROUTE,
      });
    });
  };

  return {
    state: {
      refillResponse,
      isSubmitting,
      selectedProducts,
      hasSelectedAnyProducts,
      __testing: {
        productSelection,
      },
    },
    actions: {
      submitOrder,
      validateOrder,
      enqueueError,
      refetchRefillResponse,
      __testing: {
        setIsSubmitting,
      },
    },
  };
};
